import { css } from '@emotion/react'
import { colors, Divider, font } from 'plume-ui'
import { useRef, useState } from 'react'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'
import { statusSettingPath } from '@/lib/url'
import { useUser } from '../../../../../contexts/AuthContext'
import { useOnClickOutside } from '../../../../../hooks/useOnClickOutside'

import {
  STATUS_SETTING_ON_STATUS_PANEL,
  SUM_OF_STATUS_SETTING,
  trackEvent,
} from '@/lib/tracking'
import { User } from '../../../../../types'
import { Status } from '../../../../../types/api'
import { StatusPanelSection } from './StatusPanelSection'

type Props = {
  shortName: string
  statuses: Status[]
  selectedStatusId?: number
  setSelectedStatusId: (id?: number) => void
  onSelectStatus: (companyId: string, status: Status, user: User) => void
  onSelectStatusBlank: (companyId: string, user: User) => void
}

export const StatusSection: React.FC<Props> = ({
  shortName,
  statuses,
  selectedStatusId,
  setSelectedStatusId,
  onSelectStatus,
  onSelectStatusBlank,
}) => {
  const currentUser = useUser()

  const [statusesOpening, setStatusesOpening] = useState(false)

  const statusesRef = useRef<HTMLDivElement>(null)
  useOnClickOutside([statusesRef], () =>
    setTimeout(() => setStatusesOpening(false), 100)
  )

  const handleChangeCompanyStatus = async (status: Status) => {
    onSelectStatus(shortName, status, currentUser)
    setSelectedStatusId(parseInt(status.id))
    trackEvent(currentUser, STATUS_SETTING_ON_STATUS_PANEL)
    trackEvent(currentUser, SUM_OF_STATUS_SETTING)
  }

  const handleDeleteCompanyStatus = async () => {
    onSelectStatusBlank(shortName, currentUser)
    setSelectedStatusId(undefined)
    trackEvent(currentUser, STATUS_SETTING_ON_STATUS_PANEL)
    trackEvent(currentUser, SUM_OF_STATUS_SETTING)
  }

  const selectedStatus = statuses.find(
    (s) => parseInt(s.id) === selectedStatusId
  )

  return (
    <StatusPanelSection label="ステータス">
      <div
        css={css`
          position: relative;
        `}
        ref={statusesRef}
      >
        <button
          css={css`
            display: flex;
            border: none;
            align-items: center;
            padding: 7px 4px 7px 10px;
            gap: 4px;
            border-radius: 3px;
            cursor: pointer;
            background-color: ${statusesOpening
              ? colors.gray40
              : 'transparent'};
            &:hover {
              background-color: ${colors.gray40};
            }
            position: relative;
          `}
          onClick={() => setStatusesOpening(true)}
        >
          <div
            css={css`
              height: 24px;
              min-width: 24px;
              max-width: 24px;
              background-color: ${selectedStatus?.color};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: ${font.weight.normal};
              font-size: 11px;
              line-height: 125%;
              color: ${colors.white00};
              ${!selectedStatus && `border: 1px dashed ${colors.gray70};`}
            `}
            className={'status-symbol'}
          >
            {selectedStatus ? (
              selectedStatus.symbol
            ) : (
              <IcomoonIcon
                name="check1"
                css={css`
                  color: ${colors.gray70};
                  font-size: 16px;
                `}
              />
            )}
          </div>
          <div
            css={css`
              ${!selectedStatus && `color: ${colors.gray70}`};
              font-size: 14px;
            `}
            className="status-value"
          >
            {selectedStatus ? selectedStatus.name : '（空白）'}
          </div>
          <IcomoonIcon
            name="arrow1s-down"
            css={css`
              color: ${colors.black30};
              font-size: 20px;
            `}
          />
        </button>
        {statusesOpening && (
          <ul
            css={css`
              width: 216px;
              max-height: 345px;
              margin-top: 7.5px;
              background: ${colors.white00};
              border-radius: 2px;
              box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
              padding: 5px 0;
              position: absolute;
              list-style: none;
              white-space: nowrap;
              transition: 0.2s;
              z-index: 50;
              overflow: auto;
              li:hover {
                background-color: ${colors.gray40};
              }
            `}
            className="statuses"
          >
            <li
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
              `}
              onClick={async () => {
                await handleDeleteCompanyStatus()
                setStatusesOpening(false)
              }}
            >
              {selectedStatusId === undefined && (
                <IcomoonIcon
                  name={'check1'}
                  css={css`
                    position: absolute;
                    font-size: 12px;
                    left: 12px;
                    z-index: 1;
                    color: ${colors.black30};
                  `}
                />
              )}
              <div
                css={css`
                  font-size: 14px;
                  color: ${colors.black30};
                  padding-left: 8px;
                `}
              >
                （空白）
              </div>
            </li>
            {statuses.map((status) => (
              <li
                key={status.id}
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  line-height: 125%;
                  letter-spacing: 0.02em;
                  gap: 8px;
                  font-size: 14px;
                  color: ${colors.black30};
                  cursor: pointer;
                  padding: 7px 16px 7px 36px;
                `}
                onClick={async () => {
                  await handleChangeCompanyStatus(status)
                  setStatusesOpening(false)
                }}
              >
                {parseInt(status.id) === selectedStatusId && (
                  <IcomoonIcon
                    name={'check1'}
                    css={css`
                      position: absolute;
                      font-size: 12px;
                      left: 12px;
                      z-index: 1;
                      color: ${colors.black30};
                    `}
                  />
                )}
                <div>
                  <div
                    css={css`
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      background-color: ${status.color};
                    `}
                  ></div>
                </div>
                <div
                  css={css`
                    font-size: 14px;
                    color: ${colors.black30};
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                  className="status-name"
                >
                  {status.name}
                </div>
              </li>
            ))}
            <Divider
              css={css`
                margin: 4px 0;
              `}
            />
            <li
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                line-height: 125%;
                letter-spacing: 0.02em;
                gap: 8px;
                font-size: 14px;
                color: ${colors.black30};
                cursor: pointer;
                padding: 7px 16px 7px 36px;
                position: relative;
              `}
            >
              <a
                css={css`
                  color: inherit;
                `}
                href={statusSettingPath}
                target="_blank"
              >
                <IcomoonIcon
                  name={'pen-fill'}
                  css={css`
                    position: absolute;
                    left: 12px;
                    z-index: 1;
                    color: ${colors.gray60};
                  `}
                />
                ステータス項目を編集
              </a>
            </li>
          </ul>
        )}
      </div>
    </StatusPanelSection>
  )
}
